import { gradeRank } from './grade.js';
import { fieldIndex } from './meta.js';
/** Какие операторы имеют смысл для типа данных (FilterBuilder показывает только их). */
export function operatorsFor(dataType) {
    switch (dataType) {
        case 'text':
        case 'longtext':
        case 'url':
        case 'list':
            return ['contains', 'eq', 'is_set', 'is_not_set'];
        case 'integer':
        case 'decimal':
        case 'date':
        case 'partial_date':
            return ['eq', 'gte', 'lte', 'between', 'is_set', 'is_not_set'];
        case 'year_range':
            return ['eq', 'between', 'is_set', 'is_not_set'];
        case 'boolean':
            return ['eq'];
        case 'enum':
        case 'ref':
            return ['in', 'eq', 'neq', 'is_set', 'is_not_set'];
        case 'multi_enum':
            return ['in', 'is_set', 'is_not_set'];
        case 'money':
            return ['gte', 'lte', 'is_set', 'is_not_set'];
        case 'grade':
            return ['gte', 'lte', 'is_set', 'is_not_set'];
        default: {
            const exhaustive = dataType;
            throw new Error(`Неизвестный тип данных: ${String(exhaustive)}`);
        }
    }
}
/** Проверка фильтра против метаданных: известные поля и допустимые операторы. */
export function validateFilter(meta, spec) {
    const defs = fieldIndex(meta);
    const issues = [];
    spec.and.forEach((c, index) => {
        const def = defs.get(c.field);
        if (!def)
            issues.push({ index, message: `unknown_field:${c.field}` });
        else if (!operatorsFor(def.dataType).includes(c.op))
            issues.push({ index, message: `bad_operator:${c.op}` });
        else if (!['is_set', 'is_not_set'].includes(c.op) && c.value === undefined)
            issues.push({ index, message: 'missing_value' });
        else if (!valueFits(def.dataType, c))
            issues.push({ index, message: 'bad_value' });
    });
    return issues;
}
const isNum = (x) => (typeof x === 'number' && Number.isFinite(x)) ||
    (typeof x === 'string' && x.trim() !== '' && Number.isFinite(Number(x)));
const isText = (x) => typeof x === 'string' || typeof x === 'number';
/** Форма значения под оператор и тип данных — иначе SQL получил бы NaN или упал на разборе. */
function valueFits(dataType, c) {
    const v = c.value;
    if (c.op === 'is_set' || c.op === 'is_not_set')
        return true;
    if (c.op === 'in')
        return Array.isArray(v) && v.length <= 500 && v.every(isText);
    const scalar = (x) => {
        switch (dataType) {
            case 'integer':
            case 'decimal':
            case 'year_range':
                return isNum(x);
            case 'date':
            case 'partial_date':
                return typeof x === 'string' && x.length <= 10;
            case 'boolean':
                return typeof x === 'boolean';
            case 'money':
                return typeof x === 'object' && x !== null && isNum(x.amount);
            case 'grade': {
                const g = x;
                return typeof g === 'object' && g !== null && isText(g.scale) && isText(g.value);
            }
            default:
                return isText(x);
        }
    };
    if (c.op === 'between')
        return Array.isArray(v) && v.length === 2 && v.every(scalar);
    return scalar(v);
}
const isBlank = (v) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
const norm = (v) => String(v).toLocaleLowerCase('ru');
function comparable(meta, def, value) {
    if (isBlank(value))
        return undefined;
    switch (def.dataType) {
        case 'money':
            return Number(value.amount);
        case 'grade':
            return gradeRank(meta, value);
        case 'integer':
        case 'decimal':
            return Number(value);
        default:
            return String(value);
    }
}
function matchCondition(meta, def, c, value) {
    if (c.op === 'is_set')
        return !isBlank(value);
    if (c.op === 'is_not_set')
        return isBlank(value);
    if (isBlank(value))
        return c.op === 'neq';
    if (def.dataType === 'year_range') {
        const r = value;
        const to = r.to ?? r.from;
        if (c.op === 'eq')
            return Number(c.value) >= r.from && Number(c.value) <= to;
        const [a, b] = c.value;
        return r.from <= b && to >= a;
    }
    if (def.dataType === 'multi_enum')
        return c.value.some((x) => value.includes(x));
    if (def.dataType === 'list' || ['text', 'longtext', 'url'].includes(def.dataType)) {
        const haystack = Array.isArray(value)
            ? value
                .map((x) => (typeof x === 'object' && x ? Object.values(x).join(' ') : String(x)))
                .join(' ')
            : String(value);
        return c.op === 'eq' ? norm(haystack) === norm(c.value) : norm(haystack).includes(norm(c.value));
    }
    const left = comparable(meta, def, value);
    switch (c.op) {
        case 'eq':
            return left === comparable(meta, def, c.value) || value === c.value;
        case 'neq':
            return value !== c.value;
        case 'in':
            return c.value.includes(value);
        case 'gte':
            return left !== undefined && left >= (comparable(meta, def, c.value) ?? Infinity);
        case 'lte':
            return left !== undefined && left <= (comparable(meta, def, c.value) ?? -Infinity);
        case 'between': {
            const [a, b] = c.value;
            const lo = comparable(meta, def, a);
            const hi = comparable(meta, def, b);
            return left !== undefined && lo !== undefined && hi !== undefined && left >= lo && left <= hi;
        }
        default:
            return false;
    }
}
/**
 * Применение FilterSpec к записи в памяти. Сервер переводит фильтр в SQL (этап 1.4),
 * эта реализация нужна тестам и офлайн-desktop.
 */
export function matchesFilter(meta, spec, values) {
    const defs = fieldIndex(meta);
    return spec.and.every((c) => {
        const def = defs.get(c.field);
        return def ? matchCondition(meta, def, c, values[c.field]) : false;
    });
}
//# sourceMappingURL=filter.js.map